import React from 'react';
import { Container, Typography } from "@mui/material";
import { apiSendContactForm } from "../api/contact/query/post";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Box,
    Button,
    TextField,
    ThemeProvider,
    CssBaseline,
} from "@mui/material";
import { theme } from "../mui.config"
import TryBanner from '../assets/dcnc_free_trial_v0.png';

const RegisterInterestSchema = z.object({
    name: z.string().min(1, "Name is required"),
    email: z.string().email("Invalid email address"),
    companyName: z.string() || "",
    trialRequest: z.boolean() || true,
});

type RegisterInterestFormData = z.infer<typeof RegisterInterestSchema>;

export const BuyLicensePage = () => {
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, isSubmitSuccessful },
    } = useForm<RegisterInterestFormData>({
        resolver: zodResolver(RegisterInterestSchema),
        defaultValues: {
            name: '',
            email: '',
            companyName: '',
            trialRequest: true,
        },
    });

    const onSubmit = async (data: RegisterInterestFormData) => {
        try {
            const res = await apiSendContactForm({ name: data.name, email: data.email, companyName: data.companyName });
            if (res?.status !== 200) {
                alert("Something went wrong. Please try again.");
            }
            let f = document.getElementById('trialForm');
            if(f){
                f.innerHTML = '';
            }
        } catch (err) {
            alert("Error sending message.");
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline /> {/* Apply global dark mode styles */}
            <Container  sx={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'start',
                justifyContent: {
                    xs: 'start',
                    sm: 'start',
                    md: 'center'
                },
                maxWidth: {
                    xs: '80vw',
                    sm: '80vw',
                    md: '60vw',
                    xl: '50vw'
                },
                minHeight: '80vh',
                color: 'primary',


            }}>

                <Box
                    component="img"
                    src={TryBanner}
                    sx={{
                        maxWidth: '100%',
                    }}
                    className="img-fluid mb-3 img-topper d-none d-lg-block"
                    alt="DigitalCNC Logo"
                    width="804"
                    height="596"
                />
                <Typography variant="h3" align="left" color="primary.contrastText" gutterBottom>
                    <span className="h3-mobile-aware">
                     Request A <span className="text-primary"> Digital<span className="fw-bold">CNC</span> </span>Trial License
                    </span>
                </Typography>

                <Typography variant="body2" align="left" color="primary.contrastText" gutterBottom >
                    We will send an email to you with a link to download the software and start your trial.
                </Typography>

                <Typography variant="h4" align="left" gutterBottom sx={{
                    fontSize: '1.1em',
                    padding: '40px 0px 0px 0px',
                    color: 'primary.contrastText',
                    fontWeight: 'fontWeightHeavy'
                }}>
                    Register For your 14 Day Free Trial
                </Typography>
                <Box
                    id="trialForm"
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        gap: '5px',
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    {/* Name Field */}
                    <TextField
                        fullWidth={false}
                        placeholder="Name"
                        margin="none"
                        {...register("name")}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        variant="outlined"
                        className="form-control"
                    />
                    {/* Email Field */}
                    <TextField
                        fullWidth={false}
                        placeholder="Email"
                        type="email"
                        margin="none"
                        {...register("email")}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        variant="outlined"
                        className="form-control"
                    />
                    {/* Company Name Field */}
                    <TextField
                        fullWidth={false}
                        placeholder="Company Name"
                        margin="none"
                        {...register("companyName")}
                        error={!!errors.companyName}
                        helperText={errors.companyName?.message}
                        variant="outlined"
                        className="form-control"
                    />
                    {/* Submit Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{
                            textAlign: 'left',
                            backgroundColor: 'primary.contrastText',
                            color: 'primary.main',
                            mt: 2,
                            padding: '15px',
                            fontSize: '1.1em',
                            fontWeight: 'bold',
                            borderRadius: '10px',
                            textTransform: 'none',
                            flexBasis: { xs: '100%', sm: 'calc(52.5% - 2px)' },
                        }}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Sending..." : "Request Trial"}
                    </Button>

                    {/* Success Message */}
                    {isSubmitSuccessful && (
                        <div className="alert alert-success">
                        <Typography  align="left" mt={2} >
                            Thanks for requesting a trial, We will respond shortly, please check your email.
                        </Typography>
                        </div>
                    )}
                </Box>
            </Container>
        </ThemeProvider>
    );
};


